import React, { useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';
import customerService from '../../utils/customerService';
import { useNotification } from '../../contexts/NotificationContext';

const CustomerImport = ({ data, onSave, onSkip, onBack, isLoading }) => {
  const { addNotification } = useNotification();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    skipCustomer: false,
    ...data
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleSkipCustomer = () => {
    setFormData(prev => ({ ...prev, skipCustomer: true }));
    onSave({ skipCustomer: true });
  };

  const handleAddLater = () => {
    onSave({ skipCustomer: true });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'Name is required';
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    // Basic email format check
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setSubmitting(true);
      const result = await customerService.createContact({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone?.trim() || null,
        serviceType: formData.serviceType?.trim() || null,
        customerType: 'individual',
        pipeline_stage: 'Lead',
      });

      if (!result.success) {
        addNotification(result.error || 'Failed to create contact', 'error');
        return;
      }

      addNotification('Customer added successfully', 'success');
      onSave({
        skipCustomer: false,
        createdContactId: result.data.id,
        createdContactName: result.data.name,
        createdContactEmail: result.data.email,
      });
    } catch (err) {
      addNotification(err.message || 'Something went wrong creating the contact', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="UserPlus" size={24} className="text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary mb-2">
          Add your first customer
        </h2>
        <p className="text-text-secondary">
          You can add your first customer now or skip this step and add them later from the dashboard.
        </p>
      </div>

      <div className="bg-surface-hover rounded-lg p-6">
        <div className="text-center">
          <Icon name="UserPlus" size={32} className="text-primary mx-auto mb-3" />
          <h3 className="text-lg font-medium text-text-primary mb-2">Add customer details</h3>
          <p className="text-text-secondary mb-6">Add at least a name and email. You can always edit later.</p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Full name <span className="text-error">*</span></label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Jane Doe"
              aria-invalid={!!errors.name}
            />
            {errors.name && <p className="mt-1 text-sm text-error">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Email <span className="text-error">*</span></label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="jane@example.com"
              aria-invalid={!!errors.email}
            />
            {errors.email && <p className="mt-1 text-sm text-error">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Phone</label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="(555) 555-5555"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Service type (optional)</label>
            <Input
              value={formData.serviceType}
              onChange={(e) => setFormData(prev => ({ ...prev, serviceType: e.target.value }))}
              placeholder="e.g., Fence installation"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={isLoading || submitting}
              className="w-full sm:w-auto"
            >
              {isLoading || submitting ? 'Adding…' : 'Add customer'}
            </Button>
            <Button
              variant="secondary"
              onClick={handleAddLater}
              disabled={isLoading || submitting}
              className="w-full sm:w-auto"
            >
              I'll add customers later
            </Button>
            <Button
              variant="ghost"
              onClick={onSkip}
              disabled={isLoading || submitting}
              className="w-full sm:w-auto"
            >
              Skip this step
            </Button>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-6">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={isLoading}
        >
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Go Back
        </Button>
        <Button
          variant="ghost"
          onClick={onSkip}
          disabled={isLoading}
        >
          Skip for now
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading || submitting}
          className="px-8"
        >
          {isLoading || submitting ? 'Saving...' : 'Continue'}
          <Icon name="ArrowRight" size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default CustomerImport; 